import React from 'react';
import SectionHeader from './SectionHeader';
import TextInput from './TextInput';
import SelectInput from './SelectInput';
import ChoiceRadio from './ChoiceRadio';
import AddressFields from './AddressFields';
import NotesInput from './NotesInput';
import {
  EMPLOYMENT_STATUSES,
  OCCUPATIONS,
  OCCUPATION_OTHER,
  OCCUPATION_STATUSES,
} from './options';
import { SECTION_DEFAULTS, useSectionDefaults } from './defaults';

/**
 * Employment details.
 *
 * Occupation is shown only to employed and self-employed clients — for anyone
 * not employed, retired, a homemaker or a student the whole field is hidden
 * rather than shown-and-optional. For those it is shown to it is now
 * optional: it doubles as the industry question, and the business name and
 * address answer what Client Services actually needed. Schwab's account
 * application still wants a value, so a blank one is a question at Getting
 * Started rather than a blocked form.
 *
 * Self-employed clients are asked for a business name and address too. Client
 * Services was otherwise hunting for it, or asking again at Getting Started.
 * Both statuses share the `employer_*` keys so the review step, the exported
 * PDF and the CRM mapping keep working unchanged — only the labels differ.
 */
export default function StepEmployment({ number, person = 'primary', data, onChange }) {
  useSectionDefaults(data, onChange, SECTION_DEFAULTS.employment);

  const d = data || {};
  const isSpouse = person === 'spouse';
  const p = isSpouse ? 'spouse_employment' : 'employment';
  const who = isSpouse ? "Your Spouse/Partner's" : 'Your';
  const update = (field) => (value) => onChange({ ...d, [field]: value });

  const isSelfEmployed = d.status === 'self_employed';
  const needsOccupation = OCCUPATION_STATUSES.includes(d.status);
  // Occupation is collected for exactly the statuses that also have an
  // employer or business to name, so one flag drives both.
  const needsEmployer = needsOccupation;
  const employerLabel = isSelfEmployed ? 'Business name' : 'Employer name';
  const employerAddressLabel = isSelfEmployed
    ? 'Business address'
    : 'Employer address';

  return (
    <div>
      <SectionHeader number={number} title={`${who} Employment`} />

      <ChoiceRadio
        id={`${p}-status`}
        label={<>Employment status <span className="text-danger">*</span></>}
        options={EMPLOYMENT_STATUSES}
        value={d.status}
        onChange={update('status')}
      />

      {needsOccupation && (
        <>
          <hr className="my-4" />
          <div className="row g-3">
            {needsEmployer && (
              <TextInput
                id={`${p}-employer-name`}
                label={employerLabel}
                col="col-md-6"
                required
                value={d.employer_name}
                onChange={update('employer_name')}
                autoComplete="off"
              />
            )}
            {needsOccupation && (
              <SelectInput
                id={`${p}-occupation`}
                label="Occupation (choose the option that best describes it)"
                col="col-md-6"
                options={OCCUPATIONS}
                value={d.occupation}
                onChange={update('occupation')}
              />
            )}
            {needsOccupation && d.occupation === OCCUPATION_OTHER && (
              <TextInput
                id={`${p}-occupation-other`}
                label="Please specify your occupation"
                col="col-md-6"
                required
                value={d.occupation_other}
                onChange={update('occupation_other')}
                autoComplete="off"
              />
            )}
          </div>

          {needsEmployer && (
            <div className="mt-4">
              <AddressFields
                idPrefix={`${p}-employer-addr`}
                legend={employerAddressLabel}
                required
                value={d.employer_address}
                onChange={update('employer_address')}
              />
            </div>
          )}
        </>
      )}

      <NotesInput
        value={d.notes}
        onChange={update('notes')}
        label="Anything else we should know about this section?"
        placeholder="Optional"
      />
    </div>
  );
}
